import { ArrowRight, MapPin } from "lucide-react";
import type { AcademyLocationCard } from "@/features/academy-locations/queries";
import { distance, registerHref, STATUS_LABELS } from "./format";

type Props = {
  location: AcademyLocationCard;
  /** Kilometres from the place searched for, when there is one. */
  away?: number;
};

/** One proposed location: its area photo, figures and the way to register interest in it. */
export default function LocationCard({ location, away }: Props) {
  return (
    <article className="flex flex-col overflow-hidden rounded-[18px] bg-white shadow-[0_1px_2px_rgba(0,0,0,.04)]">
      {location.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={location.image}
          alt={location.imageAlt ?? ""}
          className="aspect-[16/9] w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex aspect-[16/9] w-full flex-col items-center justify-center bg-[#e8e8ed] px-5 text-center">
          <MapPin className="mb-3 text-[#86868b]" size={28} aria-hidden />
          <p className="text-[14px] font-medium text-[#3a3a3c]">
            {location.town}
          </p>
          <p className="mt-1 text-[12px] text-das-gray">
            Local area image coming soon
          </p>
        </div>
      )}

      <div className="flex flex-1 flex-col px-6 pt-5 pb-6">
        <h3 className="text-[19px] font-semibold text-das-black">
          {location.name}
        </h3>
        <p className="mt-1 text-[14px] text-das-gray">{location.siteName}</p>
        <p className="mt-1 text-[13px] text-das-gray">
          {STATUS_LABELS[location.status]}
        </p>
        {away !== undefined && (
          <p className="mt-2 text-[13px] font-medium text-brand-blue">
            {distance(away)} away
          </p>
        )}

        {location.population !== null && (
          <>
            <p className="mt-4 text-[14px] font-medium text-das-black">
              Population {location.population.toLocaleString("en-GB")}
            </p>
            <p className="mt-0.5 text-[12px] text-das-gray">
              {[
                location.populationArea,
                "local authority area",
                location.populationYear && `mid-${location.populationYear}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </>
        )}

        <div className="mt-4 space-y-1 text-[12px] text-das-gray">
          {location.collegeDistanceKm !== null && (
            <p>
              College · {distance(location.collegeDistanceKm)} straight line
            </p>
          )}
          {location.theoryCentreDistanceKm !== null && (
            <p>
              Theory centre · {distance(location.theoryCentreDistanceKm)}{" "}
              straight line
            </p>
          )}
        </div>

        <a
          href={registerHref(location.slug)}
          className="mt-auto inline-flex items-center gap-1.5 pt-5 text-[14px] font-medium text-blue-bright hover:underline"
        >
          Register your interest <ArrowRight size={16} aria-hidden />
        </a>
      </div>
    </article>
  );
}
